import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch,
  Grid,
  Checkbox,
  CardActions,
  Divider,
  useMediaQuery,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  PlaylistPlay as BatchStartIcon,
  StopCircle as BatchStopIcon,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';

import { deviceAPI, simulationAPI, MonitorWebSocket } from '../services/api';

// ECG Profile options with descriptions
  const ECG_PROFILES = [
    { value: 'normal', label: 'Normal Sinus Rhythm', description: 'HR: 60-100 bpm, Regular rhythm' },
    { value: 'real_dgt7_normal', label: 'Real DGT7 Normal', description: 'HR: 80 bpm, Based on actual DGT7 device data' },
    { value: 'neonate', label: 'Neonate (Newborn)', description: 'HR: 120-160 bpm, Faster rate with smaller amplitudes' },
    { value: 'tachycardia', label: 'Sinus Tachycardia', description: 'HR: 100-150 bpm, Fast regular rhythm' },
    { value: 'bradycardia', label: 'Sinus Bradycardia', description: 'HR: 40-59 bpm, Slow regular rhythm' },
    { value: 'afib', label: 'Atrial Fibrillation', description: 'HR: 60-180 bpm, Irregular rhythm, no P waves' },
    { value: 'ventricular_tachycardia', label: 'Ventricular Tachycardia', description: 'HR: 150 bpm, Wide QRS, no P waves' },
    { value: 'ventricular_fibrillation', label: 'Ventricular Fibrillation', description: 'HR: 200 bpm, Chaotic irregular waves' },
    { value: 'atrial_flutter', label: 'Atrial Flutter', description: 'HR: 80 bpm, Sawtooth P waves, regular QRS' },
    { value: 'first_degree_heart_block', label: '1st Degree Heart Block', description: 'HR: 70 bpm, Prolonged PR interval' },
    { value: 'second_degree_heart_block', label: '2nd Degree Heart Block', description: 'HR: 60 bpm, Progressive PR prolongation, dropped beats' },
    { value: 'third_degree_heart_block', label: '3rd Degree Heart Block', description: 'HR: 40 bpm, Independent P and QRS rhythms' },
    { value: 'stemi', label: 'STEMI (Acute MI)', description: 'HR: 85-110 bpm, ST elevation, pathological Q waves' },
    { value: 'stroke', label: 'Stroke (CVA)', description: 'HR: 80-100 bpm, Deep T wave inversion, prolonged QT' },
  ];

const DeviceManagement = ({ showNotification }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table'); // Default: cards on mobile, table on desktop
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [ws, setWs] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    serial_number: '',
    hardware_version: '1.2.3',
    software_version: '2.4.6',
    app_version: '1.0.0.0',
    ecg_enabled: true,
    spo2_enabled: true,
    co2_enabled: true,
    vitals_enabled: true,
    alarms_enabled: true,
    ecg_profile: 'normal', // Default ECG profile
    bp_type: 'NIBP', // Default blood pressure type
    ecg_frequency: 3.5,
    spo2_frequency: 3.5,
    co2_frequency: 3.5,
    vital_interval: 20,
    alarm_interval: 10,
  });

  // Auto-switch to card view on mobile screens
  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'table');
  }, [isMobile]);

  useEffect(() => {
    loadDevices();

    // Setup WebSocket for real-time device updates
    const websocket = new MonitorWebSocket(
      handleWSMessage,
      handleWSError,
      handleWSClose
    );
    websocket.connect();
    setWs(websocket);

    // Cleanup
    return () => {
      if (websocket) {
        websocket.disconnect();
      }
    };
  }, []);

  const loadDevices = async () => {
    try {
      const data = await deviceAPI.getAll();
      setDevices(data);
    } catch (error) {
      showNotification('Failed to load devices', 'error');
      console.error('Load error:', error);
    }
  };

  const handleWSMessage = (data) => {
    console.log('WebSocket message:', data);
    setWsConnected(true);
    
    // Handle device list updates
    if (data.type === 'device_update') {
      setDevices(data.data || []);
    }
    
    // Handle status updates - update device statuses in the list
    if (data.type === 'status_update' || data.type === 'initial_status') {
      const statuses = data.data || [];
      setDevices(prevDevices => 
        prevDevices.map(device => {
          const status = statuses.find(s => s.device_id === device.id);
          if (status) {
            return { ...device, status: status.status };
          }
          return device;
        })
      );
    }
  };

  const handleWSError = (error) => {
    console.error('WebSocket error:', error);
    setWsConnected(false);
  };

  const handleWSClose = () => {
    console.log('WebSocket closed');
    setWsConnected(false);
  };

  const handleOpenDialog = (device = null) => {
    if (device) {
      setEditingDevice(device);
      setFormData(device);
    } else {
      setEditingDevice(null);
      setFormData({
        name: '',
        serial_number: `DGT7-SIM${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
        hardware_version: '1.2.3',
        software_version: '2.4.6',
        app_version: '1.0.0.0',
        ecg_enabled: true,
        spo2_enabled: true,
        co2_enabled: true,
        vitals_enabled: true,
        alarms_enabled: true,
        ecg_profile: 'normal',
        bp_type: 'NIBP',
        ecg_frequency: 3.5,
        spo2_frequency: 3.5,
        co2_frequency: 3.5,
        vital_interval: 20,
        alarm_interval: 10,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDevice(null);
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    try {
      if (editingDevice) {
        // Update existing device
        await deviceAPI.update(editingDevice.id, {
          name: formData.name,
          hardware_version: formData.hardware_version,
          software_version: formData.software_version,
          app_version: formData.app_version,
          ecg_enabled: formData.ecg_enabled,
          spo2_enabled: formData.spo2_enabled,
          co2_enabled: formData.co2_enabled,
          vitals_enabled: formData.vitals_enabled,
          alarms_enabled: formData.alarms_enabled,
          ecg_profile: formData.ecg_profile,
          bp_type: formData.bp_type,
          ecg_frequency: formData.ecg_frequency,
          spo2_frequency: formData.spo2_frequency,
          co2_frequency: formData.co2_frequency,
          vital_interval: formData.vital_interval,
          alarm_interval: formData.alarm_interval,
        });
        showNotification('Device updated successfully', 'success');
      } else {
        // Create new device
        await deviceAPI.create({
          name: formData.name,
          serial_number: formData.serial_number,
          hardware_version: formData.hardware_version,
          software_version: formData.software_version,
          app_version: formData.app_version,
          ecg_enabled: formData.ecg_enabled,
          spo2_enabled: formData.spo2_enabled,
          co2_enabled: formData.co2_enabled,
          vitals_enabled: formData.vitals_enabled,
          alarms_enabled: formData.alarms_enabled,
          ecg_profile: formData.ecg_profile,
          bp_type: formData.bp_type,
          ecg_frequency: formData.ecg_frequency,
          spo2_frequency: formData.spo2_frequency,
          co2_frequency: formData.co2_frequency,
          vital_interval: formData.vital_interval,
          alarm_interval: formData.alarm_interval,
        });
        showNotification('Device created successfully', 'success');
      }
      handleCloseDialog();
      loadDevices();
    } catch (error) {
      showNotification(
        editingDevice ? 'Failed to update device' : 'Failed to create device',
        'error'
      );
      console.error('Submit error:', error);
    }
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) {
      return;
    }

    try {
      await deviceAPI.delete(deviceId);
      showNotification('Device deleted successfully', 'success');
      loadDevices();
    } catch (error) {
      showNotification('Failed to delete device', 'error');
      console.error('Delete error:', error);
    }
  };

  const handleStartSimulation = async (deviceId) => {
    try {
      const result = await simulationAPI.start(deviceId);
      showNotification(
        result?.already_running ? 'Simulation already running' : 'Simulation started',
        'success'
      );
      loadDevices();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to start simulation';
      showNotification(detail, 'error');
      console.error('Start error:', error);
    }
  };

  const handleStopSimulation = async (deviceId) => {
    try {
      await simulationAPI.stop(deviceId);
      showNotification('Simulation stopped', 'success');
      loadDevices();
    } catch (error) {
      showNotification('Failed to stop simulation', 'error');
      console.error('Stop error:', error);
    }
  };

  const handleSelectDevice = (deviceId) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedDevices(devices.map((device) => device.id));
    } else {
      setSelectedDevices([]);
    }
  };

  const handleBatchStart = async () => {
    if (selectedDevices.length === 0) {
      showNotification('Please select devices to start', 'warning');
      return;
    }

    try {
      const response = await simulationAPI.batchStart(selectedDevices);
      const { success, failed, already_running } = response.results;
      
      let message = `Started: ${success.length}`;
      if (already_running.length > 0) message += `, Already running: ${already_running.length}`;
      if (failed.length > 0) message += `, Failed: ${failed.length}`;
      
      showNotification(message, failed.length > 0 ? 'warning' : 'success');
      setSelectedDevices([]);
      loadDevices();
    } catch (error) {
      showNotification('Failed to start batch simulation', 'error');
      console.error('Batch start error:', error);
    }
  };

  const handleBatchStop = async () => {
    if (selectedDevices.length === 0) {
      showNotification('Please select devices to stop', 'warning');
      return;
    }

    try {
      const response = await simulationAPI.batchStop(selectedDevices);
      const { success, failed, not_running } = response.results;
      
      let message = `Stopped: ${success.length}`;
      if (not_running.length > 0) message += `, Not running: ${not_running.length}`;
      if (failed.length > 0) message += `, Failed: ${failed.length}`;
      
      showNotification(message, failed.length > 0 ? 'warning' : 'success');
      setSelectedDevices([]);
      loadDevices();
    } catch (error) {
      showNotification('Failed to stop batch simulation', 'error');
      console.error('Batch stop error:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Device Management
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
          {selectedDevices.length > 0 && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<BatchStartIcon />}
                onClick={handleBatchStart}
                size="small"
                fullWidth={window.innerWidth < 600}
              >
                Start Selected ({selectedDevices.length})
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<BatchStopIcon />}
                onClick={handleBatchStop}
                size="small"
                fullWidth={window.innerWidth < 600}
              >
                Stop Selected ({selectedDevices.length})
              </Button>
            </>
          )}
          <Chip
            icon={wsConnected ? <ActiveIcon /> : <InactiveIcon />}
            label={wsConnected ? 'Live Updates' : 'Disconnected'}
            color={wsConnected ? 'success' : 'default'}
            size="small"
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            aria-label="view mode"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <ToggleButton value="card" aria-label="card view" sx={{ flex: { xs: 1, sm: 'unset' } }}>
              <Tooltip title="Card View">
                <CardViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view" sx={{ flex: { xs: 1, sm: 'unset' } }}>
              <Tooltip title="Table View">
                <TableViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
            fullWidth={window.innerWidth < 600}
          >
            Add Device
          </Button>
        </Box>
      </Box>

      {viewMode === 'card' ? (
        /* Card View */
        <Box>
          {devices.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="textSecondary" align="center">
                  No devices found. Click "Add Device" to create one.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {devices.map((device) => (
                <Grid item xs={12} sm={6} md={4} key={device.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      border: selectedDevices.includes(device.id) ? 2 : 1,
                      borderColor: selectedDevices.includes(device.id) ? 'primary.main' : 'divider'
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header with checkbox and device name */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Checkbox
                          checked={selectedDevices.includes(device.id)}
                          onChange={() => handleSelectDevice(device.id)}
                          size="small"
                          sx={{ mt: -1, ml: -1 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" component="div">
                            {device.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                            {device.serial_number}
                          </Typography>
                        </Box>
                        <Chip
                          label={device.status}
                          color={device.status === 'running' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      {/* Device Info */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            ECG Profile:
                          </Typography>
                          <Chip
                            label={device.ecg_profile || 'normal'}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Versions:
                          </Typography>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" display="block">
                              HW: {device.hardware_version}
                            </Typography>
                            <Typography variant="caption" display="block">
                              SW: {device.software_version}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Divider sx={{ my: 0.5 }} />
                        
                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                          Data Sources:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {device.ecg_enabled && <Chip label="ECG" size="small" />}
                          {device.spo2_enabled && <Chip label="SpO2" size="small" />}
                          {device.co2_enabled && <Chip label="CO2" size="small" />}
                          {device.vitals_enabled && <Chip label="Vitals" size="small" />}
                          {device.alarms_enabled && <Chip label="Alarms" size="small" />}
                        </Box>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ p: 1.5, pt: 0, gap: 0.5 }}>
                      {device.status === 'running' ? (
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<StopIcon />}
                          onClick={() => handleStopSimulation(device.id)}
                          size="small"
                          fullWidth
                        >
                          Stop
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<StartIcon />}
                          onClick={() => handleStartSimulation(device.id)}
                          size="small"
                          fullWidth
                        >
                          Start
                        </Button>
                      )}
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(device)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(device.id)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
        /* Table View */
        <Card>
          <CardContent sx={{ overflowX: 'auto' }}>
            <TableContainer component={Paper} variant="outlined" sx={{ minWidth: { xs: 300, sm: 600 } }}>
              <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedDevices.length > 0 &&
                        selectedDevices.length < devices.length
                      }
                      checked={
                        devices.length > 0 &&
                        selectedDevices.length === devices.length
                      }
                      onChange={handleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Device Name</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Serial Number</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Versions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>ECG Profile</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Frequencies</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Data Sources</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary">
                        No devices found. Click "Add Device" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  devices.map((device) => (
                    <TableRow key={device.id} selected={selectedDevices.includes(device.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDevices.includes(device.id)}
                          onChange={() => handleSelectDevice(device.id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {device.name}
                        </Typography>
                        {/* Show serial on mobile */}
                        <Typography 
                          variant="caption" 
                          sx={{ display: { xs: 'block', md: 'none' }, color: 'text.secondary' }}
                        >
                          {device.serial_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <code style={{ fontSize: '0.75rem' }}>{device.serial_number}</code>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="caption" display="block">
                          HW: {device.hardware_version}
                        </Typography>
                        <Typography variant="caption" display="block">
                          SW: {device.software_version}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={device.status}
                          color={device.status === 'running' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={device.ecg_profile || 'normal'}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="caption" display="block">
                          ECG: {device.ecg_frequency || 3.5}Hz
                        </Typography>
                        <Typography variant="caption" display="block">
                          SpO2: {device.spo2_frequency || 3.5}Hz
                        </Typography>
                        <Typography variant="caption" display="block">
                          CO2: {device.co2_frequency || 3.5}Hz
                        </Typography>
                        <Typography variant="caption" display="block">
                          Vitals: {device.vital_interval || 20}s
                        </Typography>
                        <Typography variant="caption" display="block">
                          Alarms: {device.alarm_interval || 10}s
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {device.ecg_enabled && <Chip label="ECG" size="small" />}
                          {device.spo2_enabled && <Chip label="SpO2" size="small" />}
                          {device.co2_enabled && <Chip label="CO2" size="small" />}
                          {device.vitals_enabled && <Chip label="Vitals" size="small" />}
                          {device.alarms_enabled && <Chip label="Alarms" size="small" />}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {device.status === 'running' ? (
                          <Tooltip title="Stop">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleStopSimulation(device.id)}
                            >
                              <StopIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Start">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleStartSimulation(device.id)}
                            >
                              <StartIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog(device)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(device.id)}
                            disabled={device.status === 'running'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDevice ? 'Edit Device' : 'Add New Device'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Device Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Serial Number"
                value={formData.serial_number}
                onChange={(e) => handleFormChange('serial_number', e.target.value)}
                required
                disabled={editingDevice !== null}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Hardware Version"
                value={formData.hardware_version}
                onChange={(e) => handleFormChange('hardware_version', e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Software Version"
                value={formData.software_version}
                onChange={(e) => handleFormChange('software_version', e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="App Version"
                value={formData.app_version}
                onChange={(e) => handleFormChange('app_version', e.target.value)}
                disabled={editingDevice !== null}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>ECG Profile</InputLabel>
                <Select
                  value={formData.ecg_profile || 'normal'}
                  onChange={(e) => handleFormChange('ecg_profile', e.target.value)}
                  label="ECG Profile"
                >
                  {ECG_PROFILES.map((profile) => (
                    <MenuItem key={profile.value} value={profile.value}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {profile.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {profile.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Blood Pressure Type</InputLabel>
                <Select
                  value={formData.bp_type || 'NIBP'}
                  onChange={(e) => handleFormChange('bp_type', e.target.value)}
                  label="Blood Pressure Type"
                >
                  <MenuItem value="NIBP">
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        NIBP (Non-Invasive)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Cuff-based blood pressure measurement
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="IBP">
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        IBP (Invasive)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Arterial line blood pressure measurement
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Frequencies
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ECG Frequency (Hz)"
                type="number"
                inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                value={formData.ecg_frequency || 3.5}
                onChange={(e) => handleFormChange('ecg_frequency', parseFloat(e.target.value))}
                helperText="ECG upload frequency in Hz"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="SpO2 Frequency (Hz)"
                type="number"
                inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                value={formData.spo2_frequency || 3.5}
                onChange={(e) => handleFormChange('spo2_frequency', parseFloat(e.target.value))}
                helperText="SpO2 upload frequency in Hz"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="CO2 Frequency (Hz)"
                type="number"
                inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                value={formData.co2_frequency || 3.5}
                onChange={(e) => handleFormChange('co2_frequency', parseFloat(e.target.value))}
                helperText="CO2 upload frequency in Hz"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Vital Signs Interval (sec)"
                type="number"
                inputProps={{ min: 5, max: 300, step: 1 }}
                value={formData.vital_interval || 20}
                onChange={(e) => handleFormChange('vital_interval', parseInt(e.target.value))}
                helperText="Vital signs upload interval in seconds"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Alarm Check Interval (sec)"
                type="number"
                inputProps={{ min: 1, max: 60, step: 1 }}
                value={formData.alarm_interval || 10}
                onChange={(e) => handleFormChange('alarm_interval', parseInt(e.target.value))}
                helperText="Alarm check interval in seconds"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Data Sources
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ecg_enabled}
                    onChange={(e) => handleFormChange('ecg_enabled', e.target.checked)}
                  />
                }
                label="ECG Waveforms"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.spo2_enabled}
                    onChange={(e) => handleFormChange('spo2_enabled', e.target.checked)}
                  />
                }
                label="SpO2 Data"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.co2_enabled}
                    onChange={(e) => handleFormChange('co2_enabled', e.target.checked)}
                  />
                }
                label="CO2 Capnography"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.vitals_enabled}
                    onChange={(e) => handleFormChange('vitals_enabled', e.target.checked)}
                  />
                }
                label="Vital Signs"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.alarms_enabled}
                    onChange={(e) => handleFormChange('alarms_enabled', e.target.checked)}
                  />
                }
                label="Alarms/Events"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingDevice ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceManagement;

